import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { useMemo } from "react";
import { PageSection } from "@/components/site/PageSection";
import { buildBreadcrumbSchema } from "@/lib/breadcrumbs";
import {
  CATEGORIES,
  getSearchIndex,
  searchItems,
  type SearchCategory,
} from "@/lib/search-index";

type SearchParams = { q?: string; category?: string };

export const Route = createFileRoute("/search")({
  validateSearch: (input: Record<string, unknown>): SearchParams => {
    const out: SearchParams = {};
    if (typeof input.q === "string" && input.q !== "") out.q = input.q;
    if (
      typeof input.category === "string" &&
      input.category !== "" &&
      input.category !== "All"
    )
      out.category = input.category;
    return out;
  },
  head: () => ({
    meta: [
      { title: "Search | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Search Virginia Bariatric Surgery's blog posts, patient resources, and weight-loss service pages.",
      },
      // Don't let generic search-results permutations get indexed.
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Search | Virginia Bariatric" },
        { property: "og:description", content: "Search Virginia Bariatric Surgery's blog posts, patient resources, and weight-loss service pages." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/search" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Search | Virginia Bariatric" },
        { name: "twitter:description", content: "Search Virginia Bariatric Surgery's blog posts, patient resources, and weight-loss service pages." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/search" }],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbSchema("/search")),
      },
    ] : [],
  }),
  component: SearchPage,
});

function SearchPage() {
  const search = Route.useSearch();
  const q = search.q ?? "";
  const category = search.category ?? "All";
  const navigate = useNavigate({ from: "/search" });

  const safeCategory: "All" | SearchCategory = (
    CATEGORIES as readonly string[]
  ).includes(category)
    ? (category as "All" | SearchCategory)
    : "All";

  const results = useMemo(
    () => searchItems(getSearchIndex(), q, safeCategory),
    [q, safeCategory],
  );

  const trimmedQuery = q.trim();

  return (
    <PageSection>
      <header className="mx-auto max-w-3xl text-center">
        <h1 className="text-3xl font-semibold text-brand-logo sm:text-4xl">
          Search
        </h1>
        <p className="mt-3 text-foreground/80">
          Find pages, patient resources, and articles across our site.
        </p>
      </header>

      <form
        role="search"
        aria-label="Site search"
        className="mx-auto mt-8 flex max-w-3xl flex-col gap-3 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget;
          const nextQ = (form.elements.namedItem("q") as HTMLInputElement)
            .value;
          const nextCat = (
            form.elements.namedItem("category") as HTMLSelectElement
          ).value;
          void navigate({
            search: () => ({ q: nextQ, category: nextCat }),
            replace: true,
          });
        }}
      >
        <label htmlFor="search-q" className="sr-only">
          Search
        </label>
        <input
          id="search-q"
          name="q"
          type="search"
          defaultValue={q}
          placeholder="Search sleeve, bypass, financing…"
          autoFocus
          className="flex-1 rounded-md border border-border bg-background px-4 py-3 text-base text-foreground shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
        <label htmlFor="search-category" className="sr-only">
          Category
        </label>
        <select
          id="search-category"
          name="category"
          defaultValue={safeCategory}
          className="rounded-md border border-border bg-background px-3 py-3 text-base text-foreground shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c === "All" ? "All content" : c}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="rounded-md bg-brand-logo px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-deep focus:outline-none focus:ring-2 focus:ring-brand/40"
        >
          Search
        </button>
      </form>

      <section aria-live="polite" className="mx-auto mt-10 max-w-3xl">
        <p className="text-sm text-foreground/70">
          {trimmedQuery ? (
            <>
              {results.length} result{results.length === 1 ? "" : "s"} for{" "}
              <strong>&ldquo;{trimmedQuery}&rdquo;</strong>
              {safeCategory !== "All" ? <> in {safeCategory}</> : null}
            </>
          ) : (
            <>
              Browse {results.length} pages
              {safeCategory !== "All" ? <> in {safeCategory}</> : null}, or type
              a query above.
            </>
          )}
        </p>

        {results.length === 0 ? (
          <div className="mt-6 rounded-md border border-border bg-muted/30 p-6 text-foreground/80">
            <p>No matches. Try a different keyword or category.</p>
            <p className="mt-2 text-sm">
              Popular pages:{" "}
              <Link to="/services/sleeve" className="text-brand-logo underline">
                Sleeve Gastrectomy
              </Link>
              {" · "}
              <Link
                to="/resources/qualifications"
                className="text-brand-logo underline"
              >
                Bariatric Surgery Requirements
              </Link>
              {" · "}
              <Link to="/resources/faq" className="text-brand-logo underline">
                FAQ
              </Link>
            </p>
          </div>
        ) : (
          <ul className="mt-6 space-y-5">
            {results.map((r) => (
              <li
                key={r.url}
                className="rounded-md border border-border/60 bg-background/60 p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <Link
                    to={r.url}
                    className="text-lg font-semibold text-brand-logo hover:text-brand-deep hover:underline"
                  >
                    {r.title}
                  </Link>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-foreground/70">
                    {r.category}
                  </span>
                </div>
                <p className="mt-1 text-sm text-foreground/60">
                  virginiabariatric.com{r.url}
                </p>
                <p className="mt-2 text-foreground/85">{r.description}</p>
                {r.date ? (
                  <p className="mt-1 text-xs text-foreground/60">
                    {new Date(r.date).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </PageSection>
  );
}
